"use client";

import { useCallback } from "react";

import {
  createAssistantSession,
  createAssistantSessionNew,
  createAssistantVoiceSession,
  sendAssistantMessage,
  submitAssistantFeedback,
  submitAssistantTaskDraft,
} from "@/lib/assistant-api";
import type {
  InstantAssistantContextCard,
  InstantAssistantHistoryTurn,
  VoiceSessionContextPayload,
} from "@/types/assistant";

import { deriveDiscoveryUpdateFromUi, mergeDiscoveryUpdates } from "./discovery-state";
import { connectVoiceGatewaySession, disconnectVoiceGatewaySession, setVoiceMutedState } from "./voice/voice-session-manager";
import { clearDraft, clearTranscript } from "./storage";

import { useSession } from "@/hooks/useSession";
import { useInstantAssistantStore } from "./useInstantAssistantStore";

type SendPromptOptions = {
  contextOptOut?: boolean;
};

function hasAuthoritativeContext(cards: InstantAssistantContextCard[]): boolean {
  return cards.some((card) => card.kind !== "route");
}

function selectSelectionMetadata(cards: InstantAssistantContextCard[]) {
  const selectionCard = cards.find((card) => {
    if (card.kind === "selection") {
      return true;
    }
    return Boolean(card.metadata?.selectionHash);
  });

  if (!selectionCard) {
    return { hash: undefined, preview: undefined };
  }

  return {
    hash: selectionCard.metadata?.selectionHash,
    preview: selectionCard.subtitle ?? undefined,
  };
}

function normalizeMetadataValue(value?: string | null) {
  if (!value) {
    return undefined;
  }
  const trimmed = value.trim();
  return trimmed.length ? trimmed : undefined;
}

function buildVoiceContextPayload(cards: InstantAssistantContextCard[]): VoiceSessionContextPayload | undefined {
  if (!cards.length) {
    return undefined;
  }
  const authoritative = cards.filter((card) => card.kind !== "route");
  const candidates = authoritative.length ? authoritative : cards;
  for (const card of candidates) {
    const metadata = card.metadata ?? {};
    const context: VoiceSessionContextPayload = {};
    const taskId = normalizeMetadataValue(metadata.taskId ?? metadata.task_id);
    if (taskId) {
      context.taskId = taskId;
    }
    const taskTitle = normalizeMetadataValue(metadata.taskTitle ?? metadata.task_title ?? card.title);
    if (taskTitle) {
      context.taskTitle = taskTitle;
    }
    const repositorySlug = normalizeMetadataValue(
      metadata.repositorySlug ?? metadata.repository ?? metadata.repoSlug,
    );
    if (repositorySlug) {
      context.repositorySlug = repositorySlug;
    }
    const notes = normalizeMetadataValue(metadata.notes ?? card.subtitle);
    if (notes) {
      context.notes = notes;
    }

    if (Object.keys(context).length > 0) {
      return context;
    }
  }

  return undefined;
}

const HISTORY_TURN_LIMIT = 12;

function collectHistoryTurns(limit: number = HISTORY_TURN_LIMIT): InstantAssistantHistoryTurn[] {
  const { messages } = useInstantAssistantStore.getState();
  if (!messages.length) {
    return [];
  }

  return messages
    .filter((message) => Boolean(message.content?.trim()))
    .slice(-limit)
    .map((message) => ({
      role: message.role,
      content: message.content,
    }));
}

export function useInstantAssistantController() {
  const { session } = useSession();
  const namespaceId = useInstantAssistantStore((state) => state.namespaceId);
  const sessionId = useInstantAssistantStore((state) => state.sessionId);
  const operator = useInstantAssistantStore((state) => ({
    id: state.operatorId,
    name: state.operatorName,
    avatarUrl: state.operatorAvatarUrl,
  }));
  const isAuthenticated = session.status === "authenticated";
  const actions = useInstantAssistantStore((state) => state.actions);

  const ensureSession = useCallback(async () => {
    if (!isAuthenticated) {
      throw new Error("Sign in to chat with Scirix.");
    }

    if (!namespaceId) {
      throw new Error("Select a workspace to talk with Scirix.");
    }

    if (sessionId) {
      return sessionId;
    }

    const response = await createAssistantSession({
      namespaceId,
      operator: {
        id: operator.id,
        displayName: operator.name,
        avatarUrl: operator.avatarUrl ?? undefined,
      },
    });

    actions.hydrateSession(response);
    return response.sessionId;
  }, [namespaceId, sessionId, operator, actions, isAuthenticated]);

  const sendPrompt = useCallback(
    async (content: string, options?: SendPromptOptions) => {
      const payload = content.trim();
      if (!payload) {
        return;
      }

      const currentSessionId = await ensureSession();
      if (!namespaceId) {
        throw new Error("Select a workspace before chatting with Scirix.");
      }

      actions.markSending(true);
      actions.setError(null);
      try {
        const { discoveryUi, pendingDiscoveryUpdate, contextCards } = useInstantAssistantStore.getState();
        const contextOptOut = Boolean(options?.contextOptOut);
        if (!hasAuthoritativeContext(contextCards) && !contextOptOut) {
          throw new Error("Attach workspace context or opt out before sending.");
        }

        const sourceRoute = typeof window !== "undefined" ? window.location.pathname : undefined;
        const discoveryPayload = mergeDiscoveryUpdates(
          pendingDiscoveryUpdate,
          deriveDiscoveryUpdateFromUi(discoveryUi),
        );
        const { hash: selectionHash, preview: selectionPreview } = selectSelectionMetadata(contextCards);
        const historyTurns = collectHistoryTurns();
        actions.setStatus({ kind: "thinking", messageId: null, queueEntryId: null, availableAtUtc: null, detail: null });
        const response = await sendAssistantMessage(currentSessionId, {
          content: payload,
          context: contextCards.length ? contextCards : undefined,
          sourceRoute,
          discovery: discoveryPayload ?? undefined,
          namespaceId,
          contextOptOut,
          selectionHash,
          selectionPreview,
          history: historyTurns.length ? historyTurns : undefined,
        });
        // The relay controller now returns 202 Accepted with no body —
        // the operator echo and assistant reply arrive over SSE, not in
        // this response. Legacy panel is unreachable from the UI today;
        // skip the body-driven UI updates when the response is empty.
        if (!response) return;
        actions.applyMessageResponse(response);
        if (response.isQueued) {
          actions.setStatus({
            kind: "queued",
            messageId: response.message.messageId,
            queueEntryId: response.queueEntryId ?? null,
            availableAtUtc: response.queuedUntilUtc ?? null,
            detail: null,
          });
        }
        if (discoveryPayload) {
          actions.clearPendingDiscoveryUpdate();
        }
      } catch (error) {
        const detail = (error as Error)?.message ?? "Unable to send your message.";
        actions.setError(detail);
        actions.setStatus({ kind: "error", messageId: null, queueEntryId: null, availableAtUtc: null, detail });
        throw error;
      } finally {
        actions.markSending(false);
      }
    },
    [ensureSession, actions, namespaceId],
  );

  const submitFeedback = useCallback(
    async (messageId: string, kind: "ThumbsUp" | "ThumbsDown" | "FlagInaccuracy", notes?: string) => {
      const currentSessionId = await ensureSession();
      await submitAssistantFeedback(currentSessionId, { messageId, kind, notes });
    },
    [ensureSession],
  );

  const submitTaskDraft = useCallback(
    async (payload: { title: string; summary: string; acceptanceCriteria: string }) => {
      const currentSessionId = await ensureSession();
      await submitAssistantTaskDraft(currentSessionId, payload);
    },
    [ensureSession],
  );

  const startNewSession = useCallback(async () => {
    if (!isAuthenticated) {
      throw new Error("Sign in before starting a new chat.");
    }

    if (!namespaceId) {
      throw new Error("Select a workspace before starting a new chat.");
    }

    const { discoveryUi, pendingDiscoveryUpdate } = useInstantAssistantStore.getState();
    const discoveryPayload = mergeDiscoveryUpdates(
      pendingDiscoveryUpdate,
      deriveDiscoveryUpdateFromUi(discoveryUi),
    );

    try {
      actions.setError(null);
      const response = await createAssistantSessionNew({
        namespaceId,
        operator: {
          id: operator.id,
          displayName: operator.name,
          avatarUrl: operator.avatarUrl ?? undefined,
        },
        discovery: discoveryPayload ?? undefined,
      });

      actions.resetState();
      await Promise.allSettled([
        clearTranscript(namespaceId, operator.id),
        clearDraft(namespaceId, operator.id),
      ]);
      actions.hydrateSession(response);
      if (discoveryPayload) {
        actions.clearPendingDiscoveryUpdate();
      }
      return response.sessionId;
    } catch (error) {
      const detail = (error as Error)?.message ?? "Unable to start a new chat.";
      actions.setError(detail);
      throw error;
    }
  }, [namespaceId, operator.id, operator.name, operator.avatarUrl, actions, isAuthenticated]);

  const startVoiceSession = useCallback(async () => {
    const currentSessionId = await ensureSession();
    if (!namespaceId) {
      throw new Error("Select a workspace before starting voice.");
    }

    const { contextCards, voiceSession } = useInstantAssistantStore.getState();
    actions.setVoiceConnecting(true);
    actions.setVoiceError(null);

    try {
      if (voiceSession) {
        await disconnectVoiceGatewaySession({ reason: "refresh" });
      }

      const contextPayload = buildVoiceContextPayload(contextCards);
      const response = await createAssistantVoiceSession(currentSessionId, {
        namespaceId,
        context: contextPayload,
      });
      actions.setVoiceSession(response);
      await connectVoiceGatewaySession(response.session, namespaceId);
      return response;
    } catch (error) {
      try {
        await disconnectVoiceGatewaySession({ reason: "connection_failed" });
      } catch {
        // ignore
      }
      actions.setVoiceSession(null);
      const detail = (error as Error)?.message ?? "Unable to start voice session.";
      actions.setError(detail);
      throw error;
    } finally {
      actions.setVoiceConnecting(false);
    }
  }, [ensureSession, namespaceId, actions]);

  const endVoiceSession = useCallback(
    async (reason = "user_hangup") => {
      try {
        await disconnectVoiceGatewaySession({ reason });
      } finally {
        actions.setVoiceSession(null);
      }
    },
    [actions],
  );

  const setVoiceMuted = useCallback((muted: boolean) => {
    setVoiceMutedState(muted);
  }, []);

  return {
    ensureSession,
    sendPrompt,
    startNewSession,
    submitFeedback,
    submitTaskDraft,
    startVoiceSession,
    endVoiceSession,
    setVoiceMuted,
  };
}
